import { v4 as uuidv4 } from 'uuid';
import { ParsedModifierGroup } from '../types/menu';
import appContainer from './container';
import { RootStore } from '../app/store';
import {
  InfoTransmissionMessage,
  messagingActions,
} from '../reducers/messagingSlice';
import { UPSELL_METRIC_VERSION } from '../constants';
import { IUpsellExperimentStamp } from '../redux/features/experiments/experiments.props';
import { buildUpsellExperimentStamp } from '../redux/features/experiments/experiments.utils';

interface IUpsellAttempted extends IUpsellExperimentStamp {
  upsell_id: string;
  upsell_intent: string;
  upsell_issued_timestamp: string;
  upsell_phrase: string;
  trigger_by: string;
  upsell_default_item: string;
  upsell_items: string[];
  upsell_decline_items: string[];
  parent_item: string;
}

// The attempt's A/B labels, carried onto every metric that settles it.
const experimentStampOf = ({
  ab_experiment,
  ab_group,
  prompt_variant,
  experiments_unknown,
}: IUpsellExperimentStamp): IUpsellExperimentStamp => ({
  ...(ab_experiment !== undefined && { ab_experiment }),
  ...(ab_group !== undefined && { ab_group }),
  ...(prompt_variant !== undefined && { prompt_variant }),
  ...(experiments_unknown !== undefined && { experiments_unknown }),
});

export class Upsell {
  upsellAttempted: IUpsellAttempted | null = null;
  upsellSettled: boolean = false;
  // HITL-1701: the constraint behind the current attempt — the key the AI's own
  // upsell_attempted carries as upsell_intent. Kept off IUpsellAttempted so the
  // wire payload is unchanged.
  attemptedConstraintName: string | null = null;
  // HITL-1701: the AI armed its own attempt for this same constraint after ours
  // was made, so it records the customer's answer and ours would count it twice.
  answerRecordedByAI: boolean = false;
  getUpsellAttempted() {
    return this.upsellAttempted;
  }
  setUpsellAttempted(value: IUpsellAttempted | null) {
    this.upsellAttempted = value;
    this.attemptedConstraintName = null;
    this.answerRecordedByAI = false;
  }
  getUpsellSettled() {
    return this.upsellSettled;
  }
  setUpsellSettled(value: boolean) {
    this.upsellSettled = value;
  }
  attempt(
    constraint: ParsedModifierGroup,
    parentItem: string,
    triggerBy: string,
    prompt: string,
    upsellName: string,
    upsellId: string = uuidv4(),
    // Session A/B stamp from selectUpsellExperimentStamp; defaults to unknown.
    experimentStamp: IUpsellExperimentStamp = buildUpsellExperimentStamp(null)
  ) {
    const {
      upsell_items = [],
      upsell_decline_items = [],
      upsell_default_item = '',
    } = constraint;
    const upsellAttempted = {
      upsell_id: upsellId,
      upsell_issued_timestamp: new Date().toISOString(),
      upsell_phrase: prompt,
      trigger_by: triggerBy,
      upsell_intent: upsellName,
      upsell_default_item,
      upsell_items,
      upsell_decline_items,
      parent_item: parentItem,
      ...experimentStamp,
    };
    this.setUpsellAttempted(upsellAttempted);
    this.attemptedConstraintName = constraint.name ?? null;
    this.setUpsellSettled(false);
    return upsellAttempted;
  }
  /**
   * HITL-1701: the AI sent an upsell_attempted of its own. When it is for the
   * constraint our open attempt was made for, the AI is now recording the same
   * offer — it scores the customer's answer itself — so ours stops recording
   * one. Returns whether the open attempt was handed to the AI.
   */
  markAttemptedByAI(constraintName: string | undefined): boolean {
    if (
      !this.upsellAttempted ||
      this.upsellSettled ||
      !constraintName ||
      constraintName !== this.attemptedConstraintName
    ) {
      return false;
    }
    this.answerRecordedByAI = true;
    return true;
  }
  reject(upsellDeclinedItems?: string[]) {
    if (this.upsellAttempted && this.answerRecordedByAI) {
      this.setUpsellSettled(true);
      return undefined;
    }
    if (this.upsellAttempted) {
      const {
        upsell_id,
        upsell_issued_timestamp,
        upsell_intent,
        parent_item,
        upsell_default_item = '',
        upsell_phrase,
      } = this.upsellAttempted;
      this.setUpsellSettled(true);
      return {
        upsell_id,
        upsell_issued_timestamp,
        upsell_intent,
        parent_item,
        upsell_phrase,
        upsell_declined_items: upsellDeclinedItems
          ? upsellDeclinedItems
          : [upsell_default_item],
        cx_utterance: '', // for data schema consistency as AI will send this field
        is_upsell_attempted: 'yes',
        is_upsell_accepted: 'no',
        ...experimentStampOf(this.upsellAttempted),
      };
    }
  }
  accept(upsellAcceptedItems: string[]) {
    if (this.upsellAttempted && this.answerRecordedByAI) {
      this.setUpsellSettled(true);
      return undefined;
    }
    if (this.upsellAttempted) {
      const {
        upsell_id,
        upsell_issued_timestamp,
        upsell_intent,
        parent_item,
        upsell_phrase,
      } = this.upsellAttempted;
      this.setUpsellSettled(true);
      return {
        upsell_id,
        upsell_issued_timestamp,
        upsell_intent,
        parent_item,
        upsell_phrase,
        upsell_accepted_items: upsellAcceptedItems,
        cx_utterance: '', // for data schema consistency as AI will send this field
        is_upsell_attempted: 'yes',
        is_upsell_accepted: 'yes',
        ...experimentStampOf(this.upsellAttempted),
      };
    }
  }

  settle() {
    if (
      this.upsellAttempted &&
      !this.upsellSettled &&
      !this.answerRecordedByAI
    ) {
      // Upsell is rejected implicitly
      const upsell_declined = this.reject();
      const store = appContainer.get<RootStore>('store');
      const message = 'Auto reject upsell';
      const payload: Partial<InfoTransmissionMessage> = {
        data: {
          type: 'METRIC',
          message: message,
          metadata: {
            upsell_declined,
            cx_utterance: '', // for data schema consistency as AI will send this field
            upsell_metric_version: UPSELL_METRIC_VERSION,
          },
        },
      };
      store?.dispatch(messagingActions.sendInfo(payload as any));
    }
    this.setUpsellAttempted(null);
  }
}

export const upsell = new Upsell();
